import React, { useState, useRef, useCallback } from "react";
import {
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  Container,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  LinearProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { apiService } from "./../services/api/ApiService";
import NotificationBanner from "./common/NotificationBanner";
import { useNavigate } from "react-router-dom";

const config = {
  chunkSize: 5 * 1024 * 1024,
};

const MAX_SIZE_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB
const validFormats = ["mp4", "mp3", "wav"];
const bucketName = process.env.REACT_APP_BUCKET_NAME || "default";
const token = sessionStorage.getItem("token") || "";

const NewTranscription: React.FC<{ setActiveItem: (item: string) => void }> = ({
  setActiveItem,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [transcriptionName, setTranscriptionName] = useState<string>("");
  const [caseNumber, setCaseNumber] = useState<string>("");
  const [category, setCategory] = useState<string>("Derecho Administrativo");
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [notification, setNotification] = useState({
    type: "error" as "success" | "error",
    title: "",
    message: "",
    open: false,
  });
  const [errors, setErrors] = useState({
    transcriptionName: "",
    caseNumber: "",
  });
  const [touched, setTouched] = useState({
    transcriptionName: false,
    caseNumber: false,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const validateField = (
    name: "transcriptionName" | "caseNumber",
    value: string
  ) => {
    const msg = value.trim()
      ? ""
      : name === "transcriptionName"
      ? "El nombre de la transcripción es obligatorio"
      : "El número de expediente es obligatorio";
    setErrors((prev) => ({ ...prev, [name]: msg }));
  };

  const isFormComplete = () =>
    transcriptionName.trim() &&
    caseNumber.trim() &&
    category &&
    files.length > 0;

  const validateFiles = useCallback((newFiles: File[]) => {
    if (files.length + newFiles.length > 1) {
      setNotification({
        type: "error",
        title: "Error",
        message: "Puedes subir un máximo de 1 archivo.",
        open: true,
      });
      return [];
    }

    const validFiles = newFiles.filter((file) => {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      const isFormatOk = validFormats.includes(fileExtension || "");
      const isSizeOk = file.size <= MAX_SIZE_BYTES;

      if (!isSizeOk) {
        setNotification({
          type: "error",
          title: "Archivo demasiado grande",
          message: `El archivo "${file.name}" supera el límite de 2 GB.`,
          open: true,
        });
      }

      if (!isFormatOk) {
        setNotification({
          type: "error",
          title: "Formato no soportado",
          message: `El archivo "${file.name}" tiene un formato no soportado. Formatos permitidos: ${validFormats.join(", ").toUpperCase()}.`,
          open: true,
        });
      }

      return isFormatOk && isSizeOk;
    });

    return validFiles;
  }, [files.length]);

  const addFiles = useCallback((newFiles: File[]) => {
    const validFiles = validateFiles(newFiles);
    if (validFiles.length > 0) {
      setFiles(prevFiles => [...prevFiles, ...validFiles]);
    }
  }, [validateFiles]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      addFiles(newFiles);
      event.currentTarget.value = "";
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Solo quitar el estado de drag over si realmente salimos del área
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      addFiles(droppedFiles);
    }
  }, [addFiles]);

  const handleFileRemove = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleUploadAreaClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSubmit = async () => {
    let hasError = false;
    const newErrors = { transcriptionName: "", caseNumber: "" };

    if (!transcriptionName.trim()) {
      newErrors.transcriptionName =
        "El nombre de la transcripción es obligatorio";
      hasError = true;
    }

    if (!caseNumber.trim()) {
      newErrors.caseNumber = "El número de expediente es obligatorio";
      hasError = true;
    }

    setErrors(newErrors);

    if (hasError) return;

    if (files.length === 0) {
      setNotification({
        type: "error",
        title: "Archivo requerido",
        message: "Debes subir al menos un archivo de audio o video.",
        open: true,
      });
      return;
    }

    const file = files[0];
    if (file.size > MAX_SIZE_BYTES) {
      setNotification({
        type: "error",
        title: "Archivo demasiado grande",
        message: `El archivo supera el límite de 2 GB.`,
        open: true,
      });
      return;
    }
    const totalChunks = Math.ceil(file.size / config.chunkSize);

    setIsUploading(true);
    setErrorMessage("");
    setUploadProgress(0);

    try {
      // Upload chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * config.chunkSize;
        const end = Math.min(start + config.chunkSize, file.size);
        const blob = file.slice(start, end);

        await apiService.uploadChunk(file, i, totalChunks, bucketName, blob);
        setUploadProgress(((i + 1) / totalChunks) * 100);
        console.log(`Chunk ${i + 1} of ${totalChunks} uploaded successfully`);
      }

      // Finalize upload
      await apiService.finalizeUpload(
        file.name,
        totalChunks,
        bucketName,
        transcriptionName,
        caseNumber
      );
      console.log("File upload finalized successfully");

      setActiveItem("Mis Transcripciones");
      navigate("/listTranscriptor");

      setUploadProgress(100);
      setNotification({
        type: "success",
        title: "Éxito",
        message: "Transcripción creada exitosamente.",
        open: true,
      });
    } catch (error: any) {
      if (error.response && error.response.status === 409) {
        setNotification({
          type: "error",
          title: "Error",
          message:
            "Ya existe un video con el mismo nombre. Por favor, elige otro nombre o video.",
          open: true,
        });
      } else {
        setNotification({
          type: "error",
          title: "Error",
          message: "Error al generar la transcripción. Intenta de nuevo.",
          open: true,
        });
      }
      console.error("Error uploading file:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ p: 4, backgroundColor: "white", borderRadius: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Transcriptor/Nueva transcripción
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontFamily: "Montserrat",
              fontSize: "40px",
              fontWeight: 600,
              lineHeight: "48.76px",
              textAlign: "left",
            }}
            gutterBottom
          >
            Nueva transcripción
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            paragraph
            sx={{ mb: 3 }}
          >
            Esta herramienta está específicamente diseñada para transcribir
            sesiones judiciales. Para utilizarla correctamente, ingresa el
            nombre de la sesión, el número de expediente y selecciona la
            categoría correspondiente. Luego, sube el archivo de audio o video
            relacionado de la sesión judicial, una vez procesado, el resultado
            estará disponible en la pestaña "Mis transcripciones".
          </Typography>

          <Box component="form" noValidate autoComplete="off" sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  required
                  fullWidth
                  label="Nombre de la transcripción"
                  variant="outlined"
                  margin="normal"
                  value={transcriptionName}
                  onChange={(e) => {
                    setTranscriptionName(e.target.value);
                    if (touched.transcriptionName)
                      validateField("transcriptionName", e.target.value);
                  }}
                  onBlur={(e) => {
                    setTouched((prev) => ({
                      ...prev,
                      transcriptionName: true,
                    }));
                    validateField("transcriptionName", e.target.value);
                  }}
                  error={Boolean(errors.transcriptionName)}
                  helperText={errors.transcriptionName}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  required
                  fullWidth
                  label="Número de expediente"
                  variant="outlined"
                  margin="normal"
                  value={caseNumber}
                  onChange={(e) => {
                    setCaseNumber(e.target.value);
                    if (touched.caseNumber)
                      validateField("caseNumber", e.target.value);
                  }}
                  onBlur={(e) => {
                    setTouched((prev) => ({ ...prev, caseNumber: true }));
                    validateField("caseNumber", e.target.value);
                  }}
                  error={Boolean(errors.caseNumber)}
                  helperText={errors.caseNumber}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Select
                  fullWidth
                  displayEmpty
                  variant="outlined"
                  margin="dense"
                  sx={{
                    height: "56px",
                    mt: 2,
                    mb: 1,
                    fontStyle: "normal",
                    fontWeight: "normal",
                  }}
                  value={category}
                  onChange={(e) => setCategory(e.target.value as string)}
                >
                  <MenuItem disabled value="" sx={{ fontStyle: "normal" }}>
                    <em>Categoría</em>
                  </MenuItem>
                  <MenuItem value="Derecho Penal">Derecho Penal</MenuItem>
                  <MenuItem value="Derecho Civil">Derecho Civil</MenuItem>
                  <MenuItem value="Derecho Mercantil">
                    Derecho Mercantil
                  </MenuItem>
                  <MenuItem value="Derecho Administrativo">
                    Derecho Administrativo
                  </MenuItem>
                  <MenuItem value="Derecho Laboral">Derecho Laboral</MenuItem>
                  <MenuItem value="Derecho Constitucional">
                    Derecho Constitucional
                  </MenuItem>
                  <MenuItem value="Derecho Internacional">
                    Derecho Internacional
                  </MenuItem>
                  <MenuItem value="Derecho Ambiental">
                    Derecho Ambiental
                  </MenuItem>
                  <MenuItem value="Derecho Agrario">Derecho Agrario</MenuItem>
                </Select>
              </Grid>
            </Grid>

            {/* Área de carga de archivos mejorada con drag & drop */}
            <Box
              sx={{
                border: isDragOver ? "2px dashed #1976d2" : "2px dashed #e0e0e0",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                mb: 3,
                backgroundColor: isDragOver ? "#f3f8ff" : "#f9f9f9",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: isDragOver ? "#f3f8ff" : "#f0f0f0",
                  borderColor: "#1976d2",
                },
              }}
              onClick={handleUploadAreaClick}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                accept="audio/*,video/*"
                style={{ display: "none" }}
                id="file-upload"
                type="file"
                onChange={handleFileUpload}
              />
              
              <CloudUploadIcon 
                sx={{ 
                  fontSize: 50, 
                  color: isDragOver ? "#1976d2" : "#9e9e9e",
                  transition: "color 0.3s ease"
                }} 
              />
              
              <Typography 
                variant="h6" 
                sx={{
                  color: isDragOver ? "#1976d2" : "textSecondary",
                  fontWeight: isDragOver ? 600 : "normal",
                  transition: "all 0.3s ease"
                }}
              >
                {isDragOver 
                  ? "Suelta el archivo aquí" 
                  : "Arrastra y suelta archivos aquí o haz clic para seleccionar"
                }
              </Typography>
              
              <Typography
                fontSize={12}
                sx={{
                  color: isDragOver ? "#1976d2" : "text.secondary",
                  fontFamily: "Montserrat",
                  mt: 1,
                  transition: "color 0.3s ease"
                }}
              >
                Formatos permitidos: MP3, MP4, WAV. Tamaño máximo: 2 GB.
              </Typography>
            </Box>

            {/* Lista de archivos */}
            {files.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  Archivo seleccionado:
                </Typography>
                <List sx={{ 
                  bgcolor: "background.paper", 
                  borderRadius: 1,
                  border: "1px solid #e0e0e0"
                }}>
                  {files.map((file, index) => (
                    <ListItem key={index} sx={{ py: 1 }}>
                      <ListItemText 
                        primary={file.name}
                        secondary={`${(file.size / (1024 * 1024)).toFixed(2)} MB`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleFileRemove(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {isUploading && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Progreso de subida: {Math.round(uploadProgress)}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={isUploading || !isFormComplete()}
              fullWidth
              sx={{
                height: "56px",
                fontFamily: "Montserrat",
                fontWeight: 600,
                fontSize: "20px",
                mb: 3,
              }}
            >
              {isUploading
                ? "Subiendo..."
                : "Subir Archivo y Generar Transcripción"}
            </Button>
          </Box>

          <NotificationBanner
            type={notification.type}
            title={notification.title}
            message={notification.message}
            open={notification.open}
            onClose={handleCloseNotification}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default NewTranscription;